import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Bell } from "lucide-react";
import { InboxList } from "./InboxList";
import { PageSizeSelect, ShowMoreButton, BulkDeleteButton } from "./ListControls";
import { usePageSize, usePagedList, INBOX_CHANGED_EVENT, notifyInboxChanged } from "../../utils/pagedList";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 30000;
export const INBOX_PAGE_KEY = "rv_inbox_page_size";

// Ж12/Ж19/Ж22: единый колокольчик шапки — дропдаун внутрисайтовых уведомлений, порции, удаление
export const NotificationBell = ({ onOpenTask, onOpenAll, testPrefix = "bell" }) => {
  const [open, setOpen] = useState(false);
  const [badge, setBadge] = useState(0);
  const [size, setSize] = usePageSize(INBOX_PAGE_KEY);
  const list = usePagedList(`${API}/notifications/inbox`, size);
  const ref = useRef(null);

  const loadCount = useCallback(() => {
    axios.get(`${API}/notifications/inbox/unread-count`).then((r) => setBadge(r.data.unread || 0)).catch(() => {});
  }, []);

  useEffect(() => {
    loadCount();
    const t = setInterval(loadCount, POLL_MS);
    window.addEventListener(INBOX_CHANGED_EVENT, loadCount);
    return () => { clearInterval(t); window.removeEventListener(INBOX_CHANGED_EVENT, loadCount); };
  }, [loadCount]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const toggle = async () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    const data = await list.reload();
    if (data && (data.unread || 0) > 0) {
      try {
        const m = await axios.post(`${API}/notifications/inbox/mark-read`, {});
        setBadge(m.data.unread || 0);
        notifyInboxChanged();
      } catch (e) { /* не критично */ }
    } else setBadge(0);
  };

  const removeOne = async (n) => {
    if (!window.confirm("Удалить это уведомление?")) return;
    try { const d = await list.remove(n.id); setBadge(d.unread || 0); notifyInboxChanged(); } catch (e) { /* не критично */ }
  };

  const removeRead = async () => {
    if (!window.confirm("Удалить все прочитанные уведомления?")) return;
    try {
      const r = await axios.post(`${API}/notifications/inbox/delete-read`);
      setBadge(r.data.unread || 0);
      await list.reload();
      notifyInboxChanged();
    } catch (e) { /* не критично */ }
  };

  const go = (fn) => (arg) => { setOpen(false); fn && fn(arg); };

  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={toggle} data-testid={`${testPrefix}-btn`} aria-label="Уведомления"
        className="relative p-1.5 rounded-md" style={{ background: open ? "rgba(13,148,136,0.1)" : "transparent", border: "none", cursor: "pointer", color: "var(--rv-text-secondary)" }}>
        <Bell size={18} />
        {badge > 0 && (
          <span data-testid={`${testPrefix}-unread-count`} className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full text-white flex items-center justify-center"
            style={{ background: "var(--rv-error)", fontSize: "0.6rem", fontWeight: 700 }}>{badge > 99 ? "99+" : badge}</span>
        )}
      </button>
      {open && (
        <div data-testid={`${testPrefix}-dropdown`} className="fixed left-2 right-2 top-14 sm:absolute sm:left-auto sm:right-0 sm:top-full sm:mt-1 sm:w-[380px] rounded-xl shadow-lg overflow-hidden"
          style={{ background: "white", border: "1px solid var(--rv-border)", zIndex: 10001 }}>
          <div className="px-4 py-2.5 flex flex-wrap items-center gap-x-3 gap-y-1.5" style={{ borderBottom: "1px solid var(--rv-border)" }}>
            <span className="text-sm font-semibold mr-auto" style={{ color: "var(--rv-text-primary)" }}>Уведомления на сайте</span>
            <PageSizeSelect value={size} onChange={setSize} testId={`${testPrefix}-page-size`} />
            <BulkDeleteButton label="Удалить прочитанные" onClick={removeRead} disabled={!list.items.length} testId={`${testPrefix}-delete-read`} />
          </div>
          <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
            <InboxList items={list.items} onOpenTask={go(onOpenTask)} onDelete={removeOne} testPrefix={`${testPrefix}-inbox`} />
            <ShowMoreButton shown={list.items.length} total={list.total} size={size} onClick={list.loadMore} testId={`${testPrefix}-show-more`} />
          </div>
          {onOpenAll && (
            <button type="button" onClick={go(onOpenAll)} data-testid={`${testPrefix}-open-settings`} className="w-full text-xs py-2"
              style={{ borderTop: "1px solid var(--rv-border)", background: "#F8FAFC", color: "var(--rv-teal)", cursor: "pointer" }}>
              Все уведомления и настройки
            </button>
          )}
        </div>
      )}
    </div>
  );
};
